<template>
    <div class="sidebar-form-configuration">
        <SidebarPanelHeader title="Main Form Configuration"
                            @save="save(false)"
                            @saveAndClose="save(true)"
                            @close="requestClose" />

        <!--

        <div v-if="formConfiguration.logoUrl === ''" :class="styles.FORM.FORM_GROUP">
            <label>Form Logo (Optional)</label>
            <input type="file" name="logoUrl" ref="logoUrl" @change="onFileChange">
        </div>

        <div v-else class="pb-5">
            <label>Form Logo</label>
            <div>
                <img :src="formConfiguration.logoUrl">            
                <div class="mt-5">
                    <a href="#" @click="changeLogo" class="ml-2">Change</a>
                </div>
            </div>
        </div>        

        <div :class="styles.FORM.FORM_GROUP">
            <label>Logo CSS Style (Optional)</label>
            <input type="text"
                   :class="styles.FORM.FORM_CONTROL"
                   v-model="formConfiguration.logoStyleCss">
        </div>

        <div :class="styles.FORM.FORM_GROUP">
            <label>Logo Link to Website (Optional)</label>
            <input type="text"
                   :class="styles.FORM.FORM_CONTROL"
                   v-model="formConfiguration.logoLink">
        </div>

        -->

        <div :class="styles.FORM.FORM_GROUP">
            <label>Form Headline</label>
            <input type="text"
                   :class="styles.FORM.FORM_CONTROL"
                   v-model="formConfiguration.headline">
        </div>

        <div :class="styles.FORM.FORM_GROUP">
            <label>Form Sub-Headline</label>
            <input type="text"
                   :class="styles.FORM.FORM_CONTROL"
                   v-model="formConfiguration.subHeadline">
        </div>

        <div :class="styles.FORM.FORM_GROUP">
            <label>
                Show Form Headline?
                <input type="checkbox" v-model="formConfiguration.isShowHeadline">
            </label>
        </div>

        <div :class="styles.FORM.FORM_GROUP">
            <label>Submit Button Text:</label>
            <input type="text"
                   :class="styles.FORM.FORM_CONTROL"
                   v-model="formConfiguration.submitButtonText"
                   placeholder="Submit">
            <small class="form-text text-muted">
                Customize the text displayed on the submit button when users view this form.
            </small>
        </div>

        <!-- Multi-Language Translation Section -->
        <hr>
        <h5>Translations</h5>

        <div :class="styles.FORM.FORM_GROUP">
            <label class="d-block mb-2">Enable languages for this form:</label>
            <div class="language-checkboxes">
                <label v-for="lang in availableTranslationLanguages"
                       :key="lang.code"
                       class="language-checkbox-label"
                       :class="{ 'is-checked': isLanguageEnabled(lang.code) }">
                    <input type="checkbox"
                           :checked="isLanguageEnabled(lang.code)"
                           @change="toggleLanguage(lang.code, $event.target.checked)">
                    <span class="lang-code-badge">{{ lang.code.toUpperCase() }}</span>
                    <span class="lang-name">{{ lang.name }}</span>
                </label>
            </div>
            <small class="form-text text-muted mt-2 d-block">
                When enabled, patients can view this form in the selected languages.
            </small>
        </div>

        <template v-for="lang in enabledTranslationLanguages">
            <div :key="lang.code + '-section'" class="translation-language-section">
                <h6><span class="lang-code-badge lang-code-badge-sm">{{ lang.code.toUpperCase() }}</span> {{ lang.name }} Overrides</h6>

                <div :class="styles.FORM.FORM_GROUP">
                    <label>Headline ({{ lang.name }})</label>
                    <input type="text"
                           :class="styles.FORM.FORM_CONTROL"
                           :value="formConfiguration['headline' + lang.suffix]"
                           @input="formConfiguration['headline' + lang.suffix] = $event.target.value"
                           :placeholder="'Enter ' + lang.name + ' headline'">
                    <small v-if="formConfiguration['headline' + lang.suffix + 'Manual']" class="form-text text-info">
                        <i class="fa fa-pencil"></i> Manually edited
                    </small>
                </div>

                <div :class="styles.FORM.FORM_GROUP">
                    <label>Sub-Headline ({{ lang.name }})</label>
                    <input type="text"
                           :class="styles.FORM.FORM_CONTROL"
                           :value="formConfiguration['subHeadline' + lang.suffix]"
                           @input="formConfiguration['subHeadline' + lang.suffix] = $event.target.value"
                           :placeholder="'Enter ' + lang.name + ' sub-headline'">
                    <small v-if="formConfiguration['subHeadline' + lang.suffix + 'Manual']" class="form-text text-info">
                        <i class="fa fa-pencil"></i> Manually edited
                    </small>
                </div>

                <div :class="styles.FORM.FORM_GROUP">
                    <label>Submit Button Text ({{ lang.name }})</label>
                    <input type="text"
                           :class="styles.FORM.FORM_CONTROL"
                           :value="formConfiguration['submitButtonText' + lang.suffix]"
                           @input="formConfiguration['submitButtonText' + lang.suffix] = $event.target.value"
                           placeholder="">
                    <small v-if="formConfiguration['submitButtonText' + lang.suffix + 'Manual']" class="form-text text-info">
                        <i class="fa fa-pencil"></i> Manually edited
                    </small>
                </div>
            </div>
        </template>

        <template v-if="enabledTranslationLanguages.length > 0">
            <div class="auto-translate-info">
                <i class="fa fa-magic text-info"></i>
                <small class="text-muted">
                    All form labels, placeholders, and options will be <strong>automatically translated</strong> when you save.
                    You can override headline/sub-headline/submit button text above.
                </small>
            </div>
        </template>
        <hr>

        <div :class="styles.FORM.FORM_GROUP">
            <label :class="{ 'required-label-error': pdfCategoryMissing }">
                Save PDF to Open Dental Imaging module under Category:<span class="required-marker">*</span>
                <select :class="[styles.FORM.FORM_CONTROL, pdfCategoryMissing ? 'required-field-error' : '']"
                    v-model="formConfiguration.pdfCategory">
                    <option value="">--Please Select--</option>
                    <option v-for="item in formConfiguration.pdfCategories"
                            :key="item.ItemName"
                            :value="item.DefNum"
                            v-text="item.ItemName">
                    </option>
                </select>
            </label>
            <small v-if="pdfCategoryMissing" class="required-field-hint">Required — patient submissions will not save to Open Dental without this.</small>
        </div>

        <div :class="styles.FORM.FORM_GROUP">
            <label :class="{ 'required-label-error': pdfFileNameMissing }">Select file name for this form:<span class="required-marker">*</span></label>
            <input type="text" v-model="formConfiguration.pdfFileName" :class="[styles.FORM.FORM_CONTROL, pdfFileNameMissing ? 'required-field-error' : '']">
            <small v-if="pdfFileNameMissing" class="required-field-hint">Required</small>
            <small v-if="pdfFileNameWillChange" class="onprem-filename-hint">
                <i class="fa fa-info-circle"></i>
                Will be saved as: <strong>&quot;{{ sanitizedPdfFileName }}&quot;</strong>
                <span class="onprem-filename-hint-note">(some characters aren't allowed in file names)</span>
            </small>
            <p/>
            <span>Whatever file name you choose, it will save to Open Dental with the current date appended to the file name.
                <p/>For instnace, if you enter MedHx in the box above, it will be saved as MedHx_{CURRENT TIMESTAMP}.pdf under the selected Category under Imaging module in Open Dental.
            </span>
        </div>

        <div :class="styles.FORM.FORM_GROUP">
            <label>
                Save uploaded images / files with this form to Open Dental Imaging module under Category:
                <select :class="styles.FORM.FORM_CONTROL"
                    v-model="formConfiguration.attachmentCategory">
                    <option value="">--Please Select--</option>
                    <option v-for="item in formConfiguration.pdfCategories"
                            :key="item.ItemName"
                            :value="item.DefNum"
                            v-text="item.ItemName">
                    </option>
                </select>
            </label>
            <small class="form-text text-muted">Each file-upload field can override this in its own settings (Control Configuration).</small>
        </div>

        <!--
        <div :class="styles.FORM.FORM_GROUP">
            <label>
                Wrapper &#x3C;form&#x3E; outside of the Form?
                <input type="checkbox" v-model="formConfiguration.renderFormTag">
            </label>
        </div>

        <div v-show="formConfiguration.renderFormTag">

            <div :class="styles.FORM.FORM_GROUP">
                <label>Form-TAG Action URL</label>
                <input type="text"
                       :class="styles.FORM.FORM_CONTROL"
                       v-model="formConfiguration.formActionURL">
            </div>

            <div :class="styles.FORM.FORM_GROUP">
                <label>Form-TAG Method</label>
                <input type="text"
                       :class="styles.FORM.FORM_CONTROL"
                       v-model="formConfiguration.formMethod">
            </div>
        </div>

        <div :class="styles.FORM.FORM_GROUP">
            <label>
                Enable Server-side Validation?
                <input type="checkbox" v-model="formConfiguration.enableServerSideValidation">
            </label>
        </div>

        <div v-show="formConfiguration.enableServerSideValidation">
            <div :class="styles.FORM.FORM_GROUP">
                <label>Validation URL API Endpoint</label>
                <input type="text"
                       :class="styles.FORM.FORM_CONTROL"
                       v-model="formConfiguration.serverSideValidationEndpoint">
            </div>
        </div>
        -->

        <div class="buttons">
            <button :class="styles.BUTTON.PRIMARY" @click="save(false)">
                Save
            </button>
            <button :class="styles.BUTTON.INFO" @click="save(true)">
                Save & Close
            </button>
        </div>
    </div>
</template>

<script>
    import {STYLE_INJECTION_MIXIN} from "../../../mixins/style-injection-mixin";
    import {SIDEBAR_BODY_MIXIN} from "../../../mixins/sidebar-body-mixin";
    import {FORM_DEFAULT_DATA} from "../../../configs/form";
    import { LANGUAGE_CONFIG } from "../../../utils/languageHelper";
    import { sanitizeOnPremFilename } from "@/utils/sanitizeOnPremFilename";
    import SidebarPanelHeader from "./SidebarPanelHeader";
    import axios from 'axios';

// Ensure environment-aware API routing for component-level axios usage
axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

    /**
     * @property {FORM_DEFAULT_DATA} formConfiguration
     */
    export default {
        name: "SidebarFormConfiguration",
        components: {SidebarPanelHeader},
        mixins: [STYLE_INJECTION_MIXIN, SIDEBAR_BODY_MIXIN],
        data: () => ({
            // OVERRIDE IT FROM `SIDEBAR_BODY_MIXIN`
            dataKey: "formConfiguration",
            formConfiguration: Object.assign({}, FORM_DEFAULT_DATA),
        }),

        computed: {
            availableTranslationLanguages() {
                // All languages except English (English is always the base)
                return Object.entries(LANGUAGE_CONFIG)
                    .filter(([code]) => code !== 'en')
                    .map(([code, config]) => ({
                        code,
                        name: config.name,
                        flag: config.flag,
                        suffix: code.charAt(0).toUpperCase() + code.slice(1)
                    }));
            },
            enabledTranslationLanguages() {
                const supported = this.formConfiguration.supportedLanguages || [];
                return this.availableTranslationLanguages.filter(lang => supported.includes(lang.code));
            },
            pdfCategoryMissing() {
                const v = this.formConfiguration.pdfCategory;
                return v == null || String(v).trim() === '';
            },
            pdfFileNameMissing() {
                const v = this.formConfiguration.pdfFileName;
                return v == null || String(v).trim() === '';
            },
            // Mirror of the backend sanitize the on-prem EXE applies to this name before the PDF
            // lands in OpenDental. Uses the same 'Patient Form' fallback as forms-service.
            sanitizedPdfFileName() {
                return sanitizeOnPremFilename(this.formConfiguration.pdfFileName, 'Patient Form');
            },
            // Non-blocking preview: only true when the staff-typed name would actually be changed by
            // the sanitizer. Blank names are handled by the separate "Required" hint, so skip them.
            pdfFileNameWillChange() {
                const raw = this.formConfiguration.pdfFileName;
                if (raw == null || String(raw).trim() === '') return false;
                return this.sanitizedPdfFileName !== String(raw);
            }
        },

        methods: {
            isLanguageEnabled(langCode) {
                const supported = this.formConfiguration.supportedLanguages || [];
                return supported.includes(langCode);
            },
            toggleLanguage(langCode, enabled) {
                if (!this.formConfiguration.supportedLanguages) {
                    this.$set(this.formConfiguration, 'supportedLanguages', ['en']);
                }
                if (enabled) {
                    if (!this.formConfiguration.supportedLanguages.includes(langCode)) {
                        this.formConfiguration.supportedLanguages.push(langCode);
                    }
                    // Legacy compat: keep spanishEnabled in sync
                    if (langCode === 'es') {
                        this.$set(this.formConfiguration, 'spanishEnabled', true);
                    }
                } else {
                    const idx = this.formConfiguration.supportedLanguages.indexOf(langCode);
                    if (idx > -1) {
                        this.formConfiguration.supportedLanguages.splice(idx, 1);
                    }
                    if (langCode === 'es') {
                        this.$set(this.formConfiguration, 'spanishEnabled', false);
                    }
                }
            },
            /**
             * Pre-Form-Validation
             * @return {String}
             */
            preSaveValidation() {
                // Only validate server-side validation endpoint if server-side validation is enabled
                if (this.formConfiguration.enableServerSideValidation && !this.formConfiguration.serverSideValidationEndpoint) {
                    return "You must include the Server-side Validation Endpoint";
                }

                // All other fields (pdfCategory, pdfFileName, attachmentCategory) are now optional
                // Form Headline is also optional as it's usually filled in anyway

                return ""
            },

            onFileChange(e) {                
                if(e.target.files) {
                    let file = e.target.files[0];
                    console.log(file);
                    var formData = new FormData();
                    console.log('formData:' + formData);                
                    //console.log(`filename: ${file.name} and encoded filename: ${encodeURIComponent(file.name)}`);                
                    //console.log(`siteID: ${window.window.localStorage.teamioSiteID}`);
                    
                    const d = {             
                        siteID: window.window.localStorage.teamioSiteID,
                        name: file.name,
                        type: file.type,
                        teamioType: 'FORM'  // wiki image, form image, etc..
                    };

                    let _this = this;

                    axios.defaults.headers.post['Content-Type'] = 'application/json';
                    return axios
                        .post('/requestUploadURL', d)
                        .then(function(response){
                            //console.log('Received response:' + response);
                            return response;
                        })
                        .then(function(j){
                            Object.keys(j.data.fields).forEach(key => {
                                formData.append(key, j.data.fields[key]);
                            });
                            formData.append("file", file);

                            return axios.post(j.data.url, formData, {
                                headers: {
                                    'Content-Type': 'multipart/form-data'
                                }
                            });
                        })
                        .then(res => {
                            console.log("result", JSON.stringify(res));
                            console.log("config", _this.formConfiguration);
                            _this.formConfiguration.logoUrl = res.headers.location;

                            /*
                            return {
                                success: 1,
                                file:{
                                    url: res.headers.location
                                }
                            } 
                            */
                        })
                        .catch(err => {
                            console.error("Error while uploading a pic:", err);
                        });         
                }
            },

            async changeLogo() {
                console.log("changeLogo");
                await this.deleteOldLogo(this.formConfiguration.logoUrl);
                this.formConfiguration.logoUrl = "";                
            },

            async deleteOldLogo(url) {
                // https://s3.amazonaws.com/teamio-clients/forms%2F4dd2fc2c-d7cb-11e9-bac0-0ad6daef1536%2F1675276898373-u+%26+me+%28test%29.png
                console.log(`in deleteOldLogo, url: ${url}`);
                let pattern = `teamio-clients\\/(forms\\%2F${window.window.localStorage.teamioSiteID}\\%2F(.*-.*))`;    
                console.log(`pattern: ${pattern}`);
                let regex = new RegExp(pattern);
                let results = regex.exec(url);
                if(results) {
                    console.dir(results);
                    let bucket;     // window.window.localStorage.teamioSiteID,
                    let fileKeys = [];

                    bucket = `${process.env.VUE_APP_TEAMIO_CLIENT_BUCKET}`;
                    fileKeys.push(results[1]);

                    const payload = {             
                        siteID: window.window.localStorage.teamioSiteID,
                        bucket: bucket,        
                        folder: 'forms',                
                        fileKeys: JSON.stringify(fileKeys),                        
                    };
                    
                    console.log(`payload: ${JSON.stringify(payload)}`);
                    axios
                        .post('/delete-files', payload)
                        .then(function(response){
                            console.log('Received response from delete old logo file:' + response);
                            return response;
                        });

                    let converted = this.convertForS3(results[1]);
                    console.log("converted:", converted);
                } else {
                    console.log('no results');
                }
            },

            convertForS3(str) {
                var encodings = {
                    "%2F": "/",
                    "+"  : " ",
                    "%26": "&",
                    '%28': "(",
                    "%29": ")",
                    };

                return str.replace(/(%2F|\+|%26|%28|%29)/gim, function (match) {
                    return encodings[match];
                });
            },

        },

        created() {
            // retrieve the data from `GlobalSidebar` passed in
            this.formConfiguration = Object.assign({}, this.formConfiguration, this.dataPackage);
            this.$refs['logoUrl'] = this.formConfiguration.logoUrl;

            // Backward compat: migrate legacy spanishEnabled to supportedLanguages
            if (!this.formConfiguration.supportedLanguages) {
                this.$set(this.formConfiguration, 'supportedLanguages', ['en']);
            }
            if (this.formConfiguration.spanishEnabled && !this.formConfiguration.supportedLanguages.includes('es')) {
                this.formConfiguration.supportedLanguages.push('es');
            }
        }
    }
</script>

<style scoped>
/* Language checkbox grid */
.language-checkboxes {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.language-checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    background: #fff;
    font-weight: normal;
    margin: 0;
}

.language-checkbox-label:hover {
    border-color: #adb5bd;
    background: #f8f9fa;
}

.language-checkbox-label.is-checked {
    border-color: #007bff;
    background: #e7f1ff;
}

.language-checkbox-label input[type="checkbox"] {
    margin: 0;
    cursor: pointer;
}

.lang-code-badge {
    display: inline-block;
    padding: 1px 6px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #fff;
    background: #6c757d;
    border-radius: 3px;
    line-height: 1.4;
}

.language-checkbox-label.is-checked .lang-code-badge {
    background: #007bff;
}

.lang-code-badge-sm {
    font-size: 10px;
    padding: 1px 5px;
}

.lang-name {
    font-size: 13px;
    color: #495057;
}

/* Auto-translate info box */
.auto-translate-info {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    background-color: #e8f4fd;
    border: 1px solid #bee5eb;
    border-radius: 4px;
    margin-top: 10px;
}

.auto-translate-info .fa {
    margin-top: 2px;
}

.auto-translate-info small {
    line-height: 1.4;
}

/* Translation override sections */
.translation-language-section {
    padding: 12px 0;
    border-top: 1px solid #e9ecef;
    margin-top: 8px;
}

.translation-language-section h6 {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 12px;
    font-weight: 600;
    font-size: 14px;
    color: #343a40;
}

.required-marker {
    color: #dc3545;
    margin-left: 2px;
}

.required-label-error {
    color: #dc3545;
}

.required-field-error {
    border-color: #dc3545;
    background-color: #fff8f8;
    animation: required-field-pulse 1.2s ease-out 3;
}

.required-field-hint {
    display: block;
    margin-top: 4px;
    color: #dc3545;
}

/* Non-blocking preview of how the file name will be sanitized for Open Dental. */
.onprem-filename-hint {
    display: block;
    margin-top: 4px;
    color: #6c757d;
    font-size: 12.5px;
    line-height: 1.4;
    word-break: break-word;
}

.onprem-filename-hint .fa {
    margin-right: 4px;
    color: #0d6efd;
}

.onprem-filename-hint strong {
    color: #343a40;
    font-weight: 600;
}

.onprem-filename-hint-note {
    color: #868e96;
    font-style: italic;
}

@keyframes required-field-pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.35);
    }
    70% {
        box-shadow: 0 0 0 6px rgba(220, 53, 69, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
    }
}
</style>